import React, { useEffect, useMemo, useState } from 'react'
import { Control, Controller, useFieldArray, useForm } from 'react-hook-form'

import { getErrorMessage } from 'utils'
import { fontOptions } from 'constants/fonts'
import { useSnackbar } from 'contexts'
import { useProposeDemo, useProvisionDemo } from 'hooks/api'
import { useSalesAccount, useSalesAccounts } from 'hooks/api/useGtmSalesPipeline'
import type { BrandImportField, ImportField, ImportFieldGap } from '@repo/types'
import { IMPORT_FIELD_LABELS, SALES_STAGE_LABELS } from '@repo/types'
import type { CommercePlatformDetection, ProvisionDemoBody, ProvisionDemoProductInput } from 'services/demosApi'
import type { SalesAccountRow } from 'services'
import { INDUSTRY_OPTIONS } from 'pages/internal/GtmOutreachPage/outreachModals'
import { ToneChip } from 'pages/internal/InternalAnalyticsPage/components/shared'
import { OutreachCertificatePanel } from 'components/feedback/OutreachCertificate'
import { COMMERCE_PLATFORM_PICKER_OPTIONS, commercePlatformDisplay } from 'constants/commercePlatforms'
import { DEMO_LIFETIME_OPTIONS, DemoLifetimeValue, lifetimeToExpiresAt, DEMO_TIER_OPTIONS} from 'constants/demos'

import { Box, IconButton, Stack, Typography } from '@mui/material'
import { CheckIcon, CopyIcon } from 'lucide-react'

import { Alert, Button, Card, Input, Modal } from '@repo/ui'
import {
  ColorPickerField,
  EMPTY_PRODUCT_ROW,
  FontSelectorField,
  ProductPicker,
  productLinkFor,
  SelectField,
  useProductImport,
} from 'components'
import type { ProductPickerFormValues } from 'components'

import { NEW_SALES_COMPANY, ReviewFormValues } from './types'

interface CreateDemoModalProps {
  open: boolean
  onClose: () => void
  /**
   * VRG-723 — open pre-picked on this GTM → Sales company (the Sales drawer and row actions): its
   * website prefills the URL, and the Company card starts on it.
   */
  initialSalesAccountId?: string | null
}

const REVIEW_DEFAULTS: ReviewFormValues = {
  salesAccountId: NEW_SALES_COMPANY,
  industry: '',
  brandName: '',
  prospectDomain: '',
  prospectContact: '',
  sendTier: 'tier_5',
  commercePlatform: '',
  colorPrimary: '#111111',
  colorSecondary: '#6B7280',
  colorBackground: '#FFFFFF',
  fontHeading: 'Inter',
  fontBody: 'Inter',
  logo: '',
  // "Always" MUST be pre-selected — VRG-311 AC5, not an empty required field.
  lifetime: 'always',
  products: [],
}

/** Best-effort brand name guess from the pasted URL — the AE edits it either way. */
const guessBrandName = (url: string): string => {
  try {
    const withScheme = /^https?:\/\//i.test(url) ? url : `https://${url}`
    const host = new URL(withScheme).hostname.replace(/^www\./, '')
    const label = host.split('.')[0] ?? host
    return label.charAt(0).toUpperCase() + label.slice(1)
  } catch {
    return ''
  }
}

/** The line under the Commerce platform field: what was detected, from what, and whether it was changed. */
const platformProvenance = (detected: CommercePlatformDetection | null, value: ReviewFormValues['commercePlatform']): string => {
  if (!detected) return value ? 'Set by you.' : 'Not detected. Pick one if you know it.'
  const what =
    detected.platform === 'custom'
      ? 'no known platform on the site'
      : detected.platform === 'unknown'
        ? 'nothing (the site could not be read)'
        : `${commercePlatformDisplay(detected.platform)}${detected.signals.length ? ` (${detected.signals.join(', ')})` : ''}`
  if (!value) return `Detected ${what}. Cleared by you.`
  return value === detected.platform ? `Detected ${what}.` : `Set by you. Detected ${what}.`
}

/** The industry list, plus a company's own value when it is not one of them (never a blank Select). */
const industryOptions = (current: string) =>
  current && !INDUSTRY_OPTIONS.some((o) => o.value === current) ? [...INDUSTRY_OPTIONS, { value: current, label: current }] : INDUSTRY_OPTIONS

/**
 * VRG-723 — what a picked sales company fills in. Only fields it actually has: a blank on the
 * company never wipes what the propose read from the site.
 */
const fromSalesAccount = (a: Pick<SalesAccountRow, 'name' | 'domain' | 'tier' | 'industry' | 'commerce_platform'>): Partial<ReviewFormValues> => ({
  brandName: a.name,
  ...(a.domain ? { prospectDomain: a.domain } : {}),
  ...(a.tier ? { sendTier: a.tier } : {}),
  industry: a.industry ?? '',
  ...(a.commerce_platform ? { commercePlatform: a.commerce_platform } : {}),
})

/** Gaps sharing a reason, merged into one line in first-seen order. */
const groupGapsByReason = <F extends ImportField>(gaps: ImportFieldGap<F>[]): Array<{ fields: F[]; reason: string }> => {
  const groups: Array<{ fields: F[]; reason: string }> = []
  for (const g of gaps) {
    const hit = groups.find((x) => x.reason === g.reason)
    if (hit) hit.fields.push(g.field)
    else groups.push({ fields: [g.field], reason: g.reason })
  }
  return groups
}

const domainFromUrl = (url: string): string => {
  try {
    const withScheme = /^https?:\/\//i.test(url) ? url : `https://${url}`
    return new URL(withScheme).hostname.replace(/^www\./, '')
  } catch {
    return url.trim()
  }
}

/**
 * VRG-311 — paste a prospect URL, review the proposed theme + products, edit
 * either, then issue. There is deliberately NO path from step 1 straight to
 * issue (AC3): `/propose` only ever returns proposals, and this modal always
 * lands on the review step before `onIssue` can be reached.
 */
export const CreateDemoModal = ({ open, onClose, initialSalesAccountId }: CreateDemoModalProps) => {
  const { open: notify } = useSnackbar()
  const [step, setStep] = useState<'url' | 'review' | 'success'>('url')
  const [url, setUrl] = useState('')
  const [themeError, setThemeError] = useState<string | null>(null)
  const [productsError, setProductsError] = useState<string | null>(null)
  const [productsFallbackReason, setProductsFallbackReason] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [demoUrl, setDemoUrl] = useState('')
  // VRG-723 — the issued demo (for the success step's outreach panel), and which company it linked.
  const [issued, setIssued] = useState<{ companyId: string; salesAccountCreated: boolean } | null>(null)
  // VRG-723 — logo, colors and fonts the propose could not pull in, and why.
  const [brandGaps, setBrandGaps] = useState<ImportFieldGap<BrandImportField>[]>([])
  // VRG-723 — the product row (useFieldArray id, stable across prepends) whose certificate is the
  // outreach certificate. Null = the first selected product.
  const [outreachRowId, setOutreachRowId] = useState<string | null>(null)
  // VRG-716 — what the propose detected, kept apart from the form value so a change by the AE
  // is recorded as `manual` and the evidence can be shown under the field.
  const [detectedPlatform, setDetectedPlatform] = useState<CommercePlatformDetection | null>(null)

  const { mutate: propose, isPending: isProposing } = useProposeDemo()
  const { mutate: provision, isPending: isIssuing } = useProvisionDemo()

  const methods = useForm<ReviewFormValues>({ defaultValues: REVIEW_DEFAULTS })
  const { control, handleSubmit, watch, reset, setValue, getValues } = methods
  const { fields, prepend, remove } = useFieldArray({ control, name: 'products' })
  // `ColorPickerField`/`FontSelectorField` type their spread props against
  // `UseFormReturn` with react-hook-form's default (untyped) `FieldValues`
  // generic (components/input/fields/types.ts) — the same shape `ThemeForm`
  // feeds them via a context-derived `useFormContext()`. Our concrete
  // `UseFormReturn<ReviewFormValues>` isn't structurally assignable to that
  // default generic, so this narrows it back for those two call sites only;
  // the rest of the form stays fully typed via `control`/`watch` above.
  const untypedFieldMethods: any = methods

  const watchedLogo = watch('logo')
  const watchedPlatform = watch('commercePlatform')
  const watchedAccountId = watch('salesAccountId')
  const watchedIndustry = watch('industry')

  // VRG-723 — GTM → Sales companies for the picker, and the picked one's leads for Contact.
  const { data: salesAccounts } = useSalesAccounts()
  const pickedId = watchedAccountId && watchedAccountId !== NEW_SALES_COMPANY ? watchedAccountId : null
  const { data: pickedAccount } = useSalesAccount(open ? pickedId ?? initialSalesAccountId ?? null : null)
  const pickedRow = salesAccounts?.find((a) => a.id === pickedId) ?? (pickedAccount?.id === pickedId ? pickedAccount : undefined)
  const companyOptions = useMemo(
    () => [
      { value: NEW_SALES_COMPANY, label: 'New company', description: 'Created on GTM → Sales with this demo, at Lead.' },
      ...(salesAccounts ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((a) => ({ value: a.id, label: a.name, rightLabel: a.domain ?? undefined })),
    ],
    [salesAccounts],
  )
  const leadOptions = (pickedAccount?.id === pickedId ? pickedAccount?.leads ?? [] : []).map((l) => ({
    value: l.email,
    label: l.name ? `${l.name} (${l.email})` : l.email,
  }))

  // Opened pre-picked: start on that company, and paste its website for the AE.
  useEffect(() => {
    if (!open || !initialSalesAccountId) return
    setValue('salesAccountId', initialSalesAccountId)
  }, [open, initialSalesAccountId, setValue])
  useEffect(() => {
    if (!open || !initialSalesAccountId || step !== 'url' || url) return
    const domain = (salesAccounts?.find((a) => a.id === initialSalesAccountId) ?? pickedAccount)?.domain
    if (domain) setUrl(domain)
    // Only the first prefill: the AE may clear or change the URL afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSalesAccountId, salesAccounts, pickedAccount])

  /** Picking a company fills name, website, tier, industry, platform from it; contact is chosen below. */
  const pickCompany = (id: string) => {
    setValue('salesAccountId', id, { shouldDirty: true })
    setValue('prospectContact', '', { shouldDirty: true })
    const row = salesAccounts?.find((a) => a.id === id)
    if (!row) return
    for (const [k, v] of Object.entries(fromSalesAccount(row))) {
      setValue(k as keyof ReviewFormValues, v as never, { shouldDirty: true })
    }
  }

  // VRG-308 AC3's paste-a-URL fallback, now shared with the Demos console's
  // Add-products modal (VRG-369). Behaviour is unchanged by the move — see
  // `useProductImport`.
  const watchedProducts = watch('products')
  const productImport = useProductImport({
    prepend,
    rows: watchedProducts,
    // VRG-716 — a store whose homepage refused us (Shopify answers bots with 429 often) still
    // proves itself Shopify when its product API answers an import. Only upgrades a value the AE
    // has not changed, and only from "could not tell" / "none found".
    onShopifyEvidence: () => {
      const untouched = (watchedPlatform ?? '') === (detectedPlatform?.platform ?? '')
      if (!untouched || detectedPlatform?.platform === 'shopify') return
      if (detectedPlatform && detectedPlatform.platform !== 'unknown' && detectedPlatform.platform !== 'custom') return
      setDetectedPlatform({ platform: 'shopify', signals: ['/products/<handle>.json'] })
      setValue('commercePlatform', 'shopify', { shouldDirty: true })
    },
  })
  const hasValidProduct = watchedProducts?.some((p) => p.selected && p.title?.trim())
  // VRG-723 — the outreach choice is among the products that will be issued, by row id.
  const selectedOutreachOptions = (watchedProducts ?? [])
    .map((p, i) => ({ p, id: fields[i]?.id }))
    .filter(({ p, id }) => id && p.selected && p.title?.trim())
    .map(({ p, id }) => ({ value: id as string, label: p.title.trim() }))
  const outreachValue = selectedOutreachOptions.some((o) => o.value === outreachRowId)
    ? (outreachRowId as string)
    : selectedOutreachOptions[0]?.value ?? ''

  const toggleProduct = (index: number) => {
    setValue(`products.${index}.selected`, !watchedProducts?.[index]?.selected, {
      shouldDirty: true,
    })
  }

  const setAllProductsSelected = (selected: boolean) => {
    watchedProducts?.forEach((_, index) => {
      setValue(`products.${index}.selected`, selected, { shouldDirty: true })
    })
  }

  const resetAll = () => {
    setStep('url')
    setUrl('')
    setThemeError(null)
    setProductsError(null)
    setProductsFallbackReason(null)
    setCopied(false)
    setDemoUrl('')
    setDetectedPlatform(null)
    setIssued(null)
    setBrandGaps([])
    setOutreachRowId(null)
    reset(REVIEW_DEFAULTS)
  }

  const handleClose = () => {
    resetAll()
    onClose()
  }

  const handlePropose = () => {
    if (!url.trim()) return
    propose(url.trim(), {
      onSuccess: (data) => {
        const theme = data.theme
        const productResult = data.products

        setThemeError(data.themeError)
        setProductsError(data.productsError)
        setProductsFallbackReason(
          productResult?.path === 'none' ? productResult.reason ?? null : null
        )
        setDetectedPlatform(data.commercePlatform ?? null)
        setBrandGaps(theme?.missing ?? [])
        setOutreachRowId(null)

        // VRG-723 — keep the picked company; with none picked, a company that already has this
        // website is picked for the AE (New company on a taken website is refused by the API).
        const proposedDomain = domainFromUrl(url)
        const keptId = getValues('salesAccountId')
        const byDomain = salesAccounts?.find((a) => a.domain && a.domain === proposedDomain.toLowerCase())
        const accountId = keptId && keptId !== NEW_SALES_COMPANY ? keptId : byDomain?.id ?? NEW_SALES_COMPANY
        const account = salesAccounts?.find((a) => a.id === accountId) ?? (pickedAccount?.id === accountId ? pickedAccount : undefined)

        reset({
          ...REVIEW_DEFAULTS,
          salesAccountId: accountId,
          brandName: guessBrandName(url),
          prospectDomain: proposedDomain,
          commercePlatform: data.commercePlatform?.platform ?? '',
          logo: theme?.logo ?? '',
          colorPrimary: theme?.colors?.primary ?? REVIEW_DEFAULTS.colorPrimary,
          colorSecondary: theme?.colors?.secondary ?? REVIEW_DEFAULTS.colorSecondary,
          colorBackground: theme?.colors?.background ?? REVIEW_DEFAULTS.colorBackground,
          fontHeading: theme?.fonts?.heading ?? REVIEW_DEFAULTS.fontHeading,
          fontBody: theme?.fonts?.body ?? REVIEW_DEFAULTS.fontBody,
          products: (productResult?.products ?? []).map((p) => ({
            title: p.title,
            sku: p.sku ?? '',
            description: p.description ?? '',
            images: p.images ?? [],
            sourceUrl: p.source?.url ?? '',
            ...productLinkFor(p.source?.url ?? '', p.title, productResult?.path, p.productUrl),
            // Everything the seeder found arrives ticked. The AE's job is to
            // spot the wrong ones, and starting from nothing selected would make
            // the common case (all five are fine) the most work.
            selected: true,
            manual: false,
            missing: p.missing ?? [],
          })),
          // The company's own facts win over guesses from the URL; the site's detection stays
          // when the company has no platform recorded.
          ...(account ? fromSalesAccount(account) : {}),
        })
        setStep('review')
      },
      onError: (error) => {
        notify({ type: 'error', title: getErrorMessage(error) })
      },
    })
  }

  const onIssue = handleSubmit((values) => {
    // Selection is what issues a product, not presence in the list. An
    // unticked row is one the AE looked at and rejected, and it stays listed
    // only so the rejection is reversible.
    const issuedRows = values.products
      .map((p, i) => ({ p, rowId: fields[i]?.id }))
      .filter(({ p }) => p.selected && p.title.trim())
    const outreachProductIndex = Math.max(0, issuedRows.findIndex(({ rowId }) => rowId === outreachRowId))
    const products: ProvisionDemoProductInput[] = issuedRows
      .map(({ p }) => ({
        title: p.title.trim(),
        sku: p.sku.trim() || undefined,
        images: p.images.length ? p.images : undefined,
        description: p.description.trim() || undefined,
        sourceUrl: p.sourceUrl.trim() || undefined,
      }))

    const body: ProvisionDemoBody = {
      brandName: values.brandName.trim(),
      prospectDomain: values.prospectDomain.trim(),
      prospectContact: values.prospectContact.trim() || undefined,
      sendTier: values.sendTier,
      theme: {
        logo: values.logo.trim() || null,
        colors: {
          primary: values.colorPrimary,
          secondary: values.colorSecondary,
          background: values.colorBackground,
        },
        fonts: { heading: values.fontHeading, body: values.fontBody },
      },
      products,
      outreachProductIndex,
      salesAccount:
        values.salesAccountId && values.salesAccountId !== NEW_SALES_COMPANY
          ? { mode: 'existing', id: values.salesAccountId, industry: values.industry || null }
          : { mode: 'new', industry: values.industry || null },
      expiresAt: lifetimeToExpiresAt(values.lifetime),
      ...(values.commercePlatform
        ? {
            commercePlatform: values.commercePlatform,
            // Left as proposed = detected; anything else is the AE's call and is never
            // overwritten by a later detection.
            commercePlatformSource: values.commercePlatform === detectedPlatform?.platform ? 'detected' : 'manual',
          }
        : {}),
    }

    provision(body, {
      onSuccess: (data) => {
        setDemoUrl(data.demoUrl)
        setIssued({ companyId: data.companyId, salesAccountCreated: data.salesAccountCreated })
        setStep('success')
      },
      onError: (error) => {
        notify({ type: 'error', title: getErrorMessage(error) })
      },
    })
  })

  const handleCopy = () => {
    navigator.clipboard.writeText(demoUrl)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Modal
      title={step === 'success' ? 'Demo ready' : 'Create demo'}
      titleAdornment={
        step !== 'url' && step !== 'success' ? (
          <Typography variant="body3" color="text.secondary">
            Review
          </Typography>
        ) : undefined
      }
      open={open}
      onClose={handleClose}
      sx={step === 'review' ? { width: 720 } : undefined}
      actions={
        step === 'url' ? (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handlePropose} loading={isProposing} disabled={!url.trim()}>
              Propose
            </Button>
          </>
        ) : step === 'review' ? (
          <>
            <Button variant="secondary" onClick={() => setStep('url')} disabled={isIssuing}>
              Back
            </Button>
            <Button onClick={onIssue} loading={isIssuing} disabled={!hasValidProduct}>
              Issue demo
            </Button>
          </>
        ) : (
          <Button onClick={handleClose}>Done</Button>
        )
      }
    >
      {step === 'url' && (
        <Stack spacing={4}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Paste the prospect's site. We'll propose a theme and products from it. Nothing
            is created until you review and issue.
          </Typography>
          <Input
            label="Prospect URL"
            placeholder="https://brand.com"
            value={url}
            onValueChange={setUrl}
            handleKeyDown={(e) => {
              if (e.key === 'Enter' && url.trim()) handlePropose()
            }}
          />
        </Stack>
      )}

      {step === 'review' && (
        <Stack spacing={5}>
          {themeError && (
            <Alert
              severity="warning"
              description={`Theme scrape degraded (${themeError}). Using defaults, so edit below before issuing.`}
            />
          )}
          {productsFallbackReason && (
            <Alert
              severity="warning"
              description={`No products found automatically (${productsFallbackReason}). Add one manually below.`}
            />
          )}
          {productsError && !productsFallbackReason && (
            <Alert
              severity="warning"
              description={`Product scrape failed (${productsError}). Add a product manually below.`}
            />
          )}

          <Card title="Company">
            <Stack spacing={4}>
              {/* VRG-723 — the GTM → Sales company the demo is for. The API links the two
                  directly, so a demo whose website differs from the company's never makes a
                  second company. */}
              <Stack spacing={2} data-testid="demo-company-picker">
                <Controller
                  name="salesAccountId"
                  control={control}
                  render={({ field }) => (
                    <SelectField
                      label="Company"
                      search
                      options={companyOptions}
                      value={field.value}
                      onChange={(v) => pickCompany(String(v))}
                    />
                  )}
                />
                {pickedRow?.deal ? (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body3" color="text.secondary">
                      Deal stage
                    </Typography>
                    <ToneChip
                      tone={pickedRow.deal.status === 'closed' ? 'negative' : pickedRow.deal.stage === 'paid_client' ? 'positive' : 'neutral'}
                      label={pickedRow.deal.status === 'closed' ? 'Closed' : SALES_STAGE_LABELS[pickedRow.deal.stage]}
                    />
                  </Stack>
                ) : (
                  <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {watchedAccountId === NEW_SALES_COMPANY
                      ? 'A new company is added to GTM → Sales with this demo, with a deal at Lead.'
                      : 'This company has no deal yet.'}
                  </Typography>
                )}
              </Stack>
              <Controller
                name="brandName"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <Input
                    label="Name"
                    placeholder="Acme"
                    value={field.value}
                    onValueChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error ? 'Required' : undefined}
                  />
                )}
              />
              <Controller
                name="prospectDomain"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <Input
                    label="Website"
                    placeholder="brand.com"
                    value={field.value}
                    onValueChange={field.onChange}
                    error={!!fieldState.error}
                  />
                )}
              />
              <Controller
                name="prospectContact"
                control={control}
                render={({ field }) =>
                  // A picked company with leads: choose one of them. Otherwise a free entry.
                  leadOptions.length ? (
                    <SelectField
                      label="Contact (optional)"
                      placeholder="Nobody"
                      options={[{ value: '', label: 'Nobody' }, ...leadOptions]}
                      value={field.value}
                      onChange={(v) => field.onChange(String(v))}
                    />
                  ) : (
                    <Input
                      label="Contact (optional)"
                      placeholder="Name or email"
                      value={field.value}
                      onValueChange={field.onChange}
                    />
                  )
                }
              />
              <Controller
                name="industry"
                control={control}
                render={({ field }) => (
                  <SelectField
                    label="Industry"
                    placeholder="Not set"
                    options={industryOptions(watchedIndustry)}
                    value={field.value}
                    onChange={(v) => field.onChange(String(v))}
                  />
                )}
              />
              <Controller
                name="sendTier"
                control={control}
                render={({ field }) => (
                  <SelectField
                    label="Tier"
                    value={field.value}
                    onChange={(v) => field.onChange(v as ReviewFormValues['sendTier'])}
                    options={[...DEMO_TIER_OPTIONS]}
                  />
                )}
              />
              {/* VRG-716 — read from the pages the propose already fetched; stored on the
                  prospect's sales company. Editable: changing it records a manual value. */}
              <Stack spacing={1}>
                <Controller
                  name="commercePlatform"
                  control={control}
                  render={({ field }) => (
                    <SelectField
                      label="Commerce platform"
                      placeholder="Not set"
                      value={field.value}
                      onChange={(v) => field.onChange(v as ReviewFormValues['commercePlatform'])}
                      options={COMMERCE_PLATFORM_PICKER_OPTIONS}
                    />
                  )}
                />
                <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }} data-testid="commerce-platform-provenance">
                  {platformProvenance(detectedPlatform, watchedPlatform)}
                </Typography>
              </Stack>
            </Stack>
          </Card>

          <Card title="Theme">
            <Stack spacing={4}>
              {brandGaps.length > 0 && (
                // VRG-723 — the brand-level field report: what the propose could not pull in.
                <Alert
                  severity="info"
                  description={
                    <Box component="ul" sx={{ m: 0, pl: 5 }} data-testid="brand-field-report">
                      {/* Fields that failed for ONE reason (the page could not be read at all) share
                          a line, rather than the same sentence three times. */}
                      {groupGapsByReason(brandGaps).map((g) => (
                        <li key={g.fields.join()}>
                          <strong>{g.fields.map((f) => IMPORT_FIELD_LABELS[f]).join(', ')}:</strong> {g.reason}
                        </li>
                      ))}
                    </Box>
                  }
                />
              )}
              {watchedLogo && (
                <Stack direction="row" spacing={3} alignItems="center">
                  <Box
                    component="img"
                    src={watchedLogo}
                    alt=""
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '8px',
                      objectFit: 'contain',
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                    onError={(e) => {
                      ;(e.target as HTMLImageElement).style.visibility = 'hidden'
                    }}
                  />
                  <Typography variant="body3" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {watchedLogo}
                  </Typography>
                </Stack>
              )}
              <Controller
                name="logo"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Logo URL"
                    placeholder="https://brand.com/logo.svg"
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                )}
              />
              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <Box sx={{ minWidth: 160, flex: 1 }}>
                  <Typography variant="body3" mb="8px">
                    Primary color
                  </Typography>
                  <ColorPickerField {...untypedFieldMethods} fieldName="colorPrimary" defaultValue={REVIEW_DEFAULTS.colorPrimary} />
                </Box>
                <Box sx={{ minWidth: 160, flex: 1 }}>
                  <Typography variant="body3" mb="8px">
                    Secondary color
                  </Typography>
                  <ColorPickerField {...untypedFieldMethods} fieldName="colorSecondary" defaultValue={REVIEW_DEFAULTS.colorSecondary} />
                </Box>
                <Box sx={{ minWidth: 160, flex: 1 }}>
                  <Typography variant="body3" mb="8px">
                    Background color
                  </Typography>
                  <ColorPickerField {...untypedFieldMethods} fieldName="colorBackground" defaultValue={REVIEW_DEFAULTS.colorBackground} />
                </Box>
              </Stack>
              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <Box sx={{ minWidth: 200, flex: 1 }}>
                  <Typography variant="body3" mb="8px">
                    Heading font
                  </Typography>
                  <FontSelectorField
                    {...untypedFieldMethods}
                    fieldName="fontHeading"
                    defaultValue={REVIEW_DEFAULTS.fontHeading}
                    options={fontOptions}
                  />
                </Box>
                <Box sx={{ minWidth: 200, flex: 1 }}>
                  <Typography variant="body3" mb="8px">
                    Body font
                  </Typography>
                  <FontSelectorField
                    {...untypedFieldMethods}
                    fieldName="fontBody"
                    defaultValue={REVIEW_DEFAULTS.fontBody}
                    options={fontOptions}
                  />
                </Box>
              </Stack>
            </Stack>
          </Card>

          <ProductPicker
            // Narrowed to the one field the picker reads. RHF's `Control<T>` is
            // invariant, so a wider form is not assignable to the narrower
            // contract even though it structurally satisfies it — same class of
            // narrowing as `untypedFieldMethods` above, and equally local.
            control={control as unknown as Control<ProductPickerFormValues>}
            fields={fields}
            rows={watchedProducts ?? []}
            onToggle={toggleProduct}
            onRemove={remove}
            onSetAllSelected={setAllProductsSelected}
            onAddManual={() => prepend(EMPTY_PRODUCT_ROW)}
            importUrl={productImport.importUrl}
            onImportUrlChange={productImport.onImportUrlChange}
            onImport={productImport.onImport}
            isImporting={productImport.isImporting}
            importError={productImport.importError}
            importNotice={productImport.importNotice}
          />

          {/* VRG-723 — the one certificate the AE sends. Provisioning makes it the demo's only
              active certificate; it can be changed later from the Demos page. */}
          <Card title="Outreach certificate">
            <Stack spacing={2}>
              <SelectField
                label="Product"
                options={selectedOutreachOptions}
                value={outreachValue}
                onChange={(v) => setOutreachRowId(String(v))}
                disabled={selectedOutreachOptions.length === 0}
                placeholder="Select a product above"
              />
              <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Its certificate is the one you send, and the only one made now. Another product gets a
                certificate if you make it the outreach certificate later.
              </Typography>
            </Stack>
          </Card>

          <Card title="Lifetime">
            <Controller
              name="lifetime"
              control={control}
              render={({ field }) => (
                <SelectField
                  value={field.value}
                  onChange={(v) => field.onChange(v as DemoLifetimeValue)}
                  options={[...DEMO_LIFETIME_OPTIONS]}
                />
              )}
            />
          </Card>
        </Stack>
      )}

      {step === 'success' && (
        <Stack spacing={4}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            The demo is live and send-ready. The email-to-view gate and location
            collection are both off, so it opens straight to the certificate.
            {issued?.salesAccountCreated ? ' The company was added to GTM → Sales at Lead.' : ' It is linked to the company on GTM → Sales.'}
          </Typography>
          <Input
            label="Demo link"
            value={demoUrl}
            readOnly
            endAdornment={
              <IconButton aria-label="Copy demo link" onClick={handleCopy} size="small">
                {copied ? (
                  <CheckIcon strokeWidth={1} size={18} />
                ) : (
                  <CopyIcon strokeWidth={1} size={18} />
                )}
              </IconButton>
            }
          />
          {issued && (
            <Card title="Outreach certificate">
              <OutreachCertificatePanel companyId={issued.companyId} />
            </Card>
          )}
        </Stack>
      )}
    </Modal>
  )
}
